import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CREDIBILITY } from "@/data/content";
import { MaskedLines, Reveal, SectionHeader } from "@/components/motion";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/lib/seo";

const IMAGE_URL =
  "https://images.unsplash.com/photo-1567943183748-3a7542120c90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZSUyMGxpbmVzfGVufDB8fHx8MTc4ODIwMjM3N3ww&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    title: "Practical Solutions",
    text: "Iconicho, LLC is an enterprise and business solutions company. We provide practical business solutions, professional services, training, marketing support, and operational resources that help organizations operate, grow, and execute. Every engagement begins with understanding the requirement — then structuring the right combination of services, resources, and execution to meet it.",
  },
  {
    title: "Professional Execution",
    text: "We approach every engagement with an emphasis on communication, organization, accountability, and follow-through. Solutions are scoped clearly, managed deliberately, and delivered with the structure that serious organizations expect.",
  },
  {
    title: "Built for the Long Term",
    text: "Iconicho is structured for durable business relationships — serving commercial businesses, organizations, government agencies, and contracting partners. As requirements evolve, our capabilities, partnerships, and contracting opportunities are built to expand with them.",
  },
];

const ParallaxImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-sm border border-charcoal/10"
      data-testid="about-image-frame"
    >
      <motion.img
        src={IMAGE_URL}
        alt="Abstract detail of modern architectural lines"
        style={{ y }}
        className="h-[320px] w-full scale-[1.2] object-cover sm:h-[420px] lg:h-[520px]"
        loading="lazy"
      />
      <div className="absolute inset-0 border-[6px] border-paper/0" />
      <div className="absolute bottom-0 left-0 flex items-center gap-3 bg-navy px-6 py-4">
        <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
        <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/80">
          Structure. Scale. Strategy.
        </span>
      </div>
    </div>
  );
};

export default function About() {
  usePageMeta(
    "About — Iconicho, LLC",
    "Iconicho, LLC is an enterprise and business solutions company built to solve business challenges across consulting, professional services, training, marketing, procurement, supply, and logistics."
  );
  return (
    <>
      <section className="grid-lines-light pt-36 lg:pt-44" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs font-medium uppercase tracking-[0.3em] text-charcoal/60">
                About Iconicho
              </span>
            </div>
          </Reveal>
          <MaskedLines
            testId="about-headline"
            className="mt-8 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
            delay={0.15}
            lines={["Built to Solve", <span key="l2">Business <span className="text-gold">Challenges.</span></span>]}
          />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-charcoal/75">
              Iconicho, LLC is a diversified enterprise and business solutions
              company — one corporate platform with the range to support
              consulting, professional services, training, marketing, business
              support, procurement, supply, and logistics requirements.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10" data-testid="about-image-section">
        <ParallaxImage />
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="about-chapters">
        <SectionHeader index="01" overline="Our Approach" title="How Iconicho Works" />
        <div className="mt-14">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.title} delay={0.05 * i}>
              <div className="grid gap-6 border-t border-charcoal/15 py-10 lg:grid-cols-12">
                <div className="lg:col-span-2">
                  <span className="font-heading text-sm font-bold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="lg:col-span-4">
                  <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">
                    {ch.title}
                  </h3>
                </div>
                <div className="lg:col-span-6">
                  <p className="leading-relaxed text-charcoal/70">{ch.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-charcoal/10" data-testid="about-credibility">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHeader
            index="02"
            overline="Why Iconicho"
            title="A Business Built for Opportunity"
          />
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {CREDIBILITY.map((item, i) => (
              <Reveal key={item.title} delay={0.08 * i}>
                <div className="border-t border-charcoal/15 pt-6">
                  <span className="font-heading text-xs font-bold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-navy">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Let's Build a Working Relationship."
        copy="Iconicho serves commercial and government opportunities alike. Start the conversation."
        testId="about-cta-band"
      />
    </>
  );
}
